import React, { useState, useEffect } from 'react';
import { Alert, Box, Snackbar, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import TopBar from '../../../components/TopBar';
import DocumentReportFilters from '../components/DocumentFilters';
import ReportTable from '../../documentReports/components/ReportTable';
import { apiClient } from '../../../utils/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import useDocumentTitle from '../../../hooks/useDocumentTitle';

const DocumentReportPage = () => {
  useDocumentTitle('Reports');
  const dispatch = useDispatch();
  const { org } = useSelector((state) => state.org);
  const userEmail = org?.user?.email || '';
  const [state, setState] = useState({
    reports: [],
    loading: false,
    totalReports: 0,
    error: null,
    page: 0,
    limit: 10,
    localSuccessSnackbarOpen: false,
  });

  const { reports, loading, totalReports, error, page, limit, localSuccessSnackbarOpen } = state;

  const fetchReports = async (newPage = 1, newLimit = 10) => {
    try {
      setState((prev) => ({ ...prev, loading: true }));
      const response = await apiClient.get('/jobs', {
        params: {
          page: newPage,
          limit: newLimit,
        },
      });
      setState((prev) => ({
        ...prev,
        reports: response.data.jobs,
        totalReports: response.data.totalJobs,
        loading: false,
      }));
    } catch (err) {
      setState((prev) => ({ ...prev, loading: false, error: err.message }));
      dispatch(
        showSnackbar({
          open: true,
          message: `Failed to fetch reports: ${err.message}`,
          severity: 'error',
        }),
      );
    }
  };

  useEffect(() => {
    fetchReports(page + 1, limit);
  }, [page, limit]);

  const handleGenerateReport = async (data) => {
    try {
      const reportData =
        data.eInvoiceType === 'aiExtraction'
          ? {
              type: 'AI_EXTRACTION_REPORTS',
              filters: {
                start_date: data.startDate,
                end_date: data.endDate,
                status: data.eInvoiceType,
              },
            }
          : {
              type: 'DOCUMENT_REPORTS',
              filters: {
                document_type: data.documentType,
                start_date: data.startDate,
                end_date: data.endDate,
                status: data.eInvoiceType,
              },
            };

      await apiClient.post('/jobs', reportData);
      const reportType = data.eInvoiceType === 'aiExtraction' ? 'AI_EXTRACTION_REPORTS' : 'DOCUMENT_REPORTS';
      await fetchReports(page + 1, limit);
      setState((prev) => ({ ...prev, localSuccessSnackbarOpen: true }));
    } catch (err) {
      setState((prev) => ({ ...prev, error: err.message }));
      dispatch(
        showSnackbar({
          open: true,
          message: `Failed to generate report: ${err.message}`,
          severity: 'error',
        }),
      );
    }
  };

  return (
    <Stack direction="column" sx={{ mt: '-1rem' }}>
      <TopBar title="Reports" />
      <Box sx={{ mt: 3 }}>
        <DocumentReportFilters onGenerateReport={handleGenerateReport} isLoading={loading} />
      </Box>
      <ReportTable
        reports={reports}
        loading={loading}
        totalReports={totalReports}
        page={page}
        limit={limit}
        onPageChange={(newPage) => setState((prev) => ({ ...prev, page: newPage }))}
        onPageSizeChange={(newLimit) => setState((prev) => ({ ...prev, limit: newLimit }))}
      />
      <Snackbar
        open={localSuccessSnackbarOpen}
        onClose={() => setState((prev) => ({ ...prev, localSuccessSnackbarOpen: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setState((prev) => ({ ...prev, localSuccessSnackbarOpen: false }))}
        >
          Report initiated successfully. We will notify{' '}
          <a href={`mailto:${userEmail}`} style={{ fontWeight: 'bold', color: 'inherit', textDecoration: 'underline' }}>
            {userEmail}
          </a>{' '}
          once it is ready.
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default DocumentReportPage;
